import 'slick-carousel/slick/slick.css';

import React from 'react';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import cx from 'classnames';
import {
  NextArrow,
  PrevArrow,
} from 'govbr.frontend.policy/components/SlickComponents/Arrows';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import Slider from 'react-slick';

import AgendaInfoCard from '../../AgendaInfoCard';
import { makeGetSubrequest } from '../PersonAgenda/personAgendaSelectors';
import {
  getConfiguredSource,
  normalizeAgendaResponse,
} from '../PersonAgenda/utils';

import styles from './AgendaSummary.module.less';

const messages = defineMessages({
  titleFallback: {
    id: 'agendaSummaryBlock.titleFallback',
    defaultMessage: 'Agenda summary',
  },
  selectAgenda: {
    id: 'agendaSummaryBlock.selectAgenda',
    defaultMessage:
      'Select an Agenda in block settings to show today appointments.',
  },
  loading: {
    id: 'agendaSummaryBlock.loading',
    defaultMessage: 'Loading appointments...',
  },
  loadError: {
    id: 'agendaSummaryBlock.loadError',
    defaultMessage: 'Unable to load agenda appointments.',
  },
  noAppointments: {
    id: 'agendaSummaryBlock.noAppointments',
    defaultMessage: 'No appointments for today.',
  },
  todayEvents: {
    id: 'agendaSummaryBlock.todayEvents',
    defaultMessage: "Today's appointments",
  },
  fullAgenda: {
    id: 'agendaSummaryBlock.fullAgenda',
    defaultMessage: 'Full agenda',
  },
});

const AGENDA_SUMMARY_METADATA_FIELDS = [
  '@id',
  'UID',
  'id',
  'title',
  'description',
  'start_date',
  'end_date',
  'location',
  'solicitante',
  'compromisso_type',
  'compromisso_type_title',
];

const DESKTOP_SLIDES = 4;
const TABLET_SLIDES = 3;
const MOBILE_SLIDES = 2;

const SAO_PAULO_TIME_ZONE = 'America/Sao_Paulo';
const SAO_PAULO_UTC_OFFSET = '-03:00';

const getTodayRangeForQuerystring = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SAO_PAULO_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = parts.find((item) => item.type === 'year')?.value;
  const month = parts.find((item) => item.type === 'month')?.value;
  const day = parts.find((item) => item.type === 'day')?.value;

  if (!year || !month || !day) {
    return [null, null];
  }

  return [
    `${year}-${month}-${day}T00:00:00${SAO_PAULO_UTC_OFFSET}`,
    `${year}-${month}-${day}T23:59:59${SAO_PAULO_UTC_OFFSET}`,
  ];
};

const parseAppointmentStart = (appointment) => {
  const rawDate = appointment?.start || appointment?.start_date;
  const parsedDate = rawDate ? new Date(rawDate) : null;
  const timestamp = parsedDate?.getTime?.();

  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
};

const AgendaSummaryBody = ({ isEditMode = false, block, data = {} }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const source = React.useMemo(() => getConfiguredSource(data), [data]);
  const sourcePath = flattenToAppURL(source?.['@id'] || '');

  const subrequestId = React.useMemo(() => {
    const baseId = block || data.block || 'agenda-summary';
    return `${baseId}-summary`;
  }, [block, data.block]);

  const agendaQuery = React.useMemo(() => {
    if (!sourcePath) return null;

    const [startOfDay, endOfDay] = getTodayRangeForQuerystring();
    const query = [
      {
        i: 'path',
        o: 'plone.app.querystring.operation.string.relativePath',
        v: '::2',
      },
      {
        i: 'portal_type',
        o: 'plone.app.querystring.operation.selection.any',
        v: ['Compromisso'],
      },
    ];

    if (startOfDay && endOfDay) {
      query.push({
        i: 'start',
        o: 'plone.app.querystring.operation.date.between',
        v: [startOfDay, endOfDay],
      });
    }

    return {
      b_size: 10000000000,
      limit: 10000000000,
      sort_on: 'start_date',
      sort_order: 'ascending',
      metadata_fields: AGENDA_SUMMARY_METADATA_FIELDS,
      query,
    };
  }, [sourcePath]);

  React.useEffect(() => {
    if (!sourcePath || !agendaQuery) return;

    dispatch(getQueryStringResults(sourcePath, agendaQuery, subrequestId));
  }, [agendaQuery, dispatch, sourcePath, subrequestId]);

  const getSubrequest = React.useMemo(() => makeGetSubrequest(), []);
  const searchResults = useSelector(
    (state) => getSubrequest(state, subrequestId) || {},
  );

  const {
    items: searchItems = [],
    loading: isAgendaLoading = false,
    loaded: hasAgendaLoaded = false,
    error: agendaError = null,
  } = searchResults;

  const normalizedAgenda = React.useMemo(() => {
    const fallbackAgenda = {
      id: source?.UID || sourcePath,
      title:
        data.title ||
        source?.Title ||
        intl.formatMessage(messages.titleFallback),
      personTitle: source?.Title || '',
      role: '',
    };

    return normalizeAgendaResponse(
      {
        agenda: {
          ...fallbackAgenda,
        },
        items: searchItems,
      },
      fallbackAgenda,
    );
  }, [data.title, intl, searchItems, source, sourcePath]);

  const appointments = React.useMemo(() => {
    return normalizedAgenda.items || [];
  }, [normalizedAgenda.items]);

  const sortedAppointments = React.useMemo(() => {
    return [...appointments].sort((a, b) => {
      return parseAppointmentStart(a) - parseAppointmentStart(b);
    });
  }, [appointments]);

  const blockTitle =
    data.title || source?.Title || intl.formatMessage(messages.titleFallback);
  const todayLabel = intl.formatDate(new Date(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: SAO_PAULO_TIME_ZONE,
  });
  const agendaErrorMessage =
    typeof agendaError === 'string'
      ? agendaError
      : agendaError?.message || intl.formatMessage(messages.loadError);
  const useLightTheme = Boolean(data?.useLightTheme);
  const useFullWidth = data?.useFullWidth !== false;
  const bgColor = useLightTheme ? 'bg-gray-cool-4' : 'bg-blue-warm-vivid-80';
  const containerClasses = cx(
    'block agenda-summary mb-4x pt-0 pb-4 pt-md-2x pb-md-6x',
    useFullWidth ? 'full-width' : 'rounder-md px-2x px-md-4x',
    useLightTheme ? styles['light-mode'] : styles['dark-mode'],
    bgColor,
  );

  const sliderSettings = React.useMemo(() => {
    return {
      accessibility: true,
      arrows: true,
      dots: true,
      nextArrow: <NextArrow outside />,
      prevArrow: <PrevArrow outside />,
      infinite: false,
      slidesToShow: DESKTOP_SLIDES,
      slidesToScroll: DESKTOP_SLIDES,
      speed: 400,
      swipeToSlide: true,
      variableWidth: false,
      responsive: [
        {
          breakpoint: 992,
          settings: {
            slidesToScroll: TABLET_SLIDES,
            slidesToShow: TABLET_SLIDES,
          },
        },
        {
          breakpoint: 768,
          settings: {
            arrows: false,
            dots: true,
            slidesToScroll: MOBILE_SLIDES,
            slidesToShow: MOBILE_SLIDES,
          },
        },
      ],
    };
  }, []);

  if (!isEditMode && !sourcePath) {
    return null;
  }

  return (
    <section className={containerClasses}>
      <div className="ui container">
        <div className="mb-5 text-center">
          <h2>{blockTitle}</h2>
          <p className="text-base m-0">
            {intl.formatMessage(messages.todayEvents)}
          </p>
          <p className="text-weight-semi-bold">{todayLabel}</p>
        </div>

        {!sourcePath ? (
          <p className="text-center">
            {intl.formatMessage(messages.selectAgenda)}
          </p>
        ) : null}

        {sourcePath && isAgendaLoading && !hasAgendaLoaded ? (
          <div role="status" aria-live="polite" className="text-center">
            <span>{intl.formatMessage(messages.loading)}</span>
          </div>
        ) : null}

        {sourcePath && agendaError ? <p>{agendaErrorMessage}</p> : null}

        {sourcePath &&
        !isAgendaLoading &&
        !agendaError &&
        sortedAppointments.length === 0 ? (
          <p className="my-6x text-center">
            {intl.formatMessage(messages.noAppointments)}
          </p>
        ) : null}

        {sortedAppointments.length > 0 ? (
          <div
            className={cx(styles.sliderScope, {
              [styles.sliderScopeDark]: !useLightTheme,
              [styles.sliderScopeLight]: useLightTheme,
            })}
          >
            {sortedAppointments.length >= DESKTOP_SLIDES ? (
              <Slider {...sliderSettings}>
                {sortedAppointments.map((appointment) => {
                  return (
                    <div key={appointment.id} className={styles.slideItem}>
                      <AgendaInfoCard
                        appointment={appointment}
                        isEditMode={isEditMode}
                      />
                    </div>
                  );
                })}
              </Slider>
            ) : (
              <div className={styles.centeredGrid}>
                {sortedAppointments.map((appointment) => {
                  return (
                    <div
                      key={appointment.id}
                      className={cx(styles.slideItem, styles.centeredGridItem)}
                    >
                      <AgendaInfoCard
                        appointment={appointment}
                        isEditMode={isEditMode}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}

        {sourcePath ? (
          <p className="text-center">
            <UniversalLink
              href={sourcePath}
              className={cx('br-button primary', {
                'dark-mode': !useLightTheme,
              })}
            >
              {intl.formatMessage(messages.fullAgenda)}
            </UniversalLink>
          </p>
        ) : null}
      </div>
    </section>
  );
};

AgendaSummaryBody.propTypes = {
  block: PropTypes.string,
  data: PropTypes.object,
};

export default AgendaSummaryBody;
